import { Link } from "react-router-dom";
import { Fuel, Mail, Shield, Phone, MessageCircle, ArrowUpRight } from "lucide-react";
import { buildWhatsAppLink, SALES_PITCHES } from "@/lib/salesContact";

import { useTranslation } from "react-i18next";
/** Multi-column app-wide footer.
 *
 * Sits at the bottom of every authenticated dashboard as well as public marketing
 * pages. Columns:
 *  - Brand: logo + one-line pitch + address
 *  - Company: legal + support links
 *  - Contact: phone / email / WhatsApp deep-link / office hours
 * Below the columns sits a slim legal bar with copyright + regional compliance line.
 * (Product column intentionally omitted — will return once we have deep docs to
 * link to. Keeps the footer feeling clean instead of thin.)
 */
export default function AppFooter() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="mt-12 border-t border-border/70 bg-gradient-to-b from-background to-secondary/30" data-testid="app-footer">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-10 sm:grid-cols-3 md:px-6">
        {/* Brand column */}
        <div>
          <Link to="/" className="inline-flex items-center gap-2" data-testid="footer-brand">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
              <Fuel className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-sm font-black tracking-tight">{t("components.appfooter.hisabx")}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Petrol · Diesel · Ledger</div>
            </div>
          </Link>
          <p className="mt-3 max-w-[260px] text-[12px] leading-relaxed text-muted-foreground">
            The digital HISAB register built <span className="font-semibold text-foreground">by dealers, for dealers</span>.
            Shift-close in 5 minutes, zero pen-and-paper.
          </p>
        </div>

        {/* Company column */}
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-widest text-foreground">{t("components.appfooter.company")}</h4>
          <ul className="mt-3 space-y-2 text-[12px]">
            <li><Link to="/pricing" className="text-muted-foreground hover:text-foreground" data-testid="footer-pricing">{t("components.appfooter.pricing")}</Link></li>
            <li><Link to="/privacy" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="footer-privacy">
              <Shield className="h-3 w-3" /> Privacy policy
            </Link></li>
            <li><Link to="/terms" className="text-muted-foreground hover:text-foreground" data-testid="footer-terms">{t("components.appfooter.terms_of_service")}</Link></li>
            <li><Link to="/delete-account" className="text-muted-foreground hover:text-foreground" data-testid="footer-delete-account">{t("components.appfooter.account_deletion")}</Link></li>
            <li>
              <a href={buildWhatsAppLink(SALES_PITCHES.enterpriseInquiry())} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="footer-enterprise">
                Enterprise <ArrowUpRight className="h-3 w-3" />
              </a>
            </li>
          </ul>
        </div>

        {/* Contact column */}
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-widest text-foreground">{t("components.appfooter.contact")}</h4>
          <ul className="mt-3 space-y-2 text-[12px]">
            <li>
              <a href={buildWhatsAppLink(SALES_PITCHES.trialSignup())} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-md bg-[#25D366] px-2.5 py-1 text-[11px] font-bold text-white hover:brightness-110" data-testid="footer-whatsapp">
                <MessageCircle className="h-3 w-3" /> WhatsApp us
              </a>
            </li>
            <li>
              <a href="tel:+917024045401" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground" data-testid="footer-phone">
                <Phone className="h-3 w-3" /> +91 70240 45401
              </a>
            </li>
            <li>
              <a href="mailto:support@hisabx.in" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground" data-testid="footer-email">
                <Mail className="h-3 w-3" /> support@hisabx.in
              </a>
            </li>
            <li className="text-[11px] text-muted-foreground">
              Support: 9 AM – 9 PM IST · Mon–Sat
            </li>
          </ul>
        </div>
      </div>

      {/* Legal bar */}
      <div className="border-t border-border/60 bg-background/50">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-4 text-[11px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between md:px-6">
          <div>© {year} HisabX Technologies · All rights reserved</div>
          <div>GST-ready · Data hosted in India</div>
        </div>
      </div>
    </footer>
  );
}
